/**
 * Modelos de texto: documentos prontos para preencher em poucos cliques.
 *
 * A ideia é simples e é a razão de existir: quem precisa de uma **ata de reunião**
 * não quer uma folha em branco — quer a ata com os campos no lugar, para preencher
 * o assunto, a data e os participantes e começar a escrever. O mesmo vale para
 * relatório, carta, ofício, declaração, recibo e plano de aula.
 *
 * Três regras que valem para todos os modelos aqui:
 *
 * 1. **Poucos campos.** Cada modelo pede de três a seis informações; o resto é
 *    estrutura que já vem escrita. Formulário de quinze campos é planilha, não
 *    modelo.
 * 2. **O texto gerado é editável de ponta a ponta.** Os modelos não travam nada:
 *    saem como HTML no editor de texto, e a pessoa ajusta o que quiser.
 * 3. **Nada de dado inventado.** Campo que a pessoa não preencheu vira um marcador
 *    entre colchetes (`[local]`), que ela vê e troca — em vez de um espaço em
 *    branco que passa despercebido no documento impresso.
 */
import { valorPorExtenso } from './valor-por-extenso.js';
import { ROTULO_DA_PESSOA, conferirDocumento, problemaDoDocumento } from './documentos-brasileiros.js';
/* ------------------------------------------------------------------ */
/* Tipos                                                              */
/* ------------------------------------------------------------------ */
export const CATEGORIAS_DE_MODELO_DE_TEXTO = ['REUNIAO', 'RELATORIO', 'DOCUMENTO', 'ESCOLA', 'PESSOAL'];
export const ROTULO_CATEGORIA_DE_MODELO = {
    REUNIAO: 'Reuniões',
    RELATORIO: 'Relatórios',
    DOCUMENTO: 'Documentos',
    ESCOLA: 'Escola e aula',
    PESSOAL: 'Pessoal',
};
/* ------------------------------------------------------------------ */
/* Ajuda                                                              */
/* ------------------------------------------------------------------ */
/** O valor escapado — o texto digitado é texto, não marcação. */
function esc(valor) {
    return (valor ?? '')
        .trim()
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * O valor preenchido — ou o marcador entre colchetes.
 *
 * O marcador é visível de propósito: quem imprime o documento vê que falta
 * preencher. Espaço em branco passa batido.
 */
function ou(valor, marcador) {
    const limpo = esc(valor);
    return limpo || `<span style="color: #b45309;">[${marcador}]</span>`;
}
function hoje() {
    return new Date().toLocaleDateString('pt-BR');
}
/** Uma lista de itens fixos (a estrutura que já vem escrita). */
function lista(itens) {
    return `<ul>${itens.map((item) => `<li>${item}</li>`).join('')}</ul>`;
}
/** Uma linha de assinatura: o nome por cima do traço. */
function assinatura(nome) {
    return `<p style="text-align: center; margin-top: 2em;">_____________________________________<br>${nome}</p>`;
}
/* ------------------------------------------------------------------ */
/* Pessoa física ou jurídica (o recibo)                               */
/* ------------------------------------------------------------------ */
export const OPCOES_DE_PESSOA = [
    { valor: 'FISICA', rotulo: ROTULO_DA_PESSOA.FISICA },
    { valor: 'JURIDICA', rotulo: ROTULO_DA_PESSOA.JURIDICA },
];
/**
 * A qualificação de quem entra no recibo: `pessoa física, CPF 529.982.247-25`.
 *
 * O tipo sai **do número**, não da escolha: quem digita 14 posições é pessoa
 * jurídica, mesmo que tenha marcado o contrário (a validação recusa esse caso, e a
 * tela corrige a marcação sozinha).
 */
export function qualificacaoDaPessoa(pessoa, documento) {
    const conferido = conferirDocumento(documento ?? '');
    if (conferido) {
        const quem = conferido.tipo === 'CPF' ? 'pessoa física' : 'pessoa jurídica';
        return `${quem}, ${conferido.tipo} ${esc(conferido.formatado)}`;
    }
    /* Número fora do formato (não deveria chegar aqui): sai como foi digitado. */
    const digitado = esc(documento);
    if (digitado)
        return digitado;
    return pessoa === 'FISICA' || pessoa === 'JURIDICA' ? ROTULO_DA_PESSOA[pessoa].toLowerCase() : '';
}
/** A assinatura do recibo, com o documento do recebedor quando houver. */
function assinaturaDoRecibo(valores) {
    const nome = esc(valores.recebedor) || '(nome e assinatura)';
    const documento = qualificacaoDaPessoa(valores.pessoaRecebedor, valores.documentoRecebedor);
    const complemento = documento ? `<br><span style="font-size: 11pt;">${documento}</span>` : '';
    return `<p style="text-align: center; margin-top: 2em;">_____________________________________<br>${nome}${complemento}</p>`;
}
/* ------------------------------------------------------------------ */
/* Os modelos                                                         */
/* ------------------------------------------------------------------ */
export const MODELOS_DE_TEXTO = [
    /* ---------------- Reuniões ---------------- */
    {
        id: 'ata-reuniao',
        nome: 'Ata de reunião',
        descricao: 'Registro do que foi discutido e decidido, com pauta e assinaturas.',
        categoria: 'REUNIAO',
        campos: [
            { id: 'assunto', rotulo: 'Assunto da reunião', tipo: 'TEXTO' },
            { id: 'data', rotulo: 'Data', tipo: 'DATA' },
            { id: 'hora', rotulo: 'Hora de início', tipo: 'HORA' },
            { id: 'local', rotulo: 'Local', tipo: 'TEXTO' },
            { id: 'participantes', rotulo: 'Participantes', dica: 'Separe por vírgula.', tipo: 'AREA' },
        ],
        titulo: (v) => `Ata de reunião${v.assunto ? ` — ${v.assunto}` : ''}`,
        montar: (v) => `
<h1 style="text-align: center;">ATA DE REUNIÃO</h1>
<p style="text-align: center;">${ou(v.assunto, 'assunto')}</p>
<p><strong>Data:</strong> ${ou(v.data, 'data')} &nbsp;&nbsp; <strong>Hora:</strong> ${ou(v.hora, 'hora')}</p>
<p><strong>Local:</strong> ${ou(v.local, 'local')}</p>
<p><strong>Participantes:</strong> ${ou(v.participantes, 'nomes dos participantes')}</p>
<h2>Pauta</h2>
${lista(['(item da pauta)', '(item da pauta)', '(item da pauta)'])}
<h2>Discussão</h2>
<p><em>Descreva o que foi discutido, na ordem da pauta.</em></p>
<h2>Deliberações</h2>
${lista(['(decisão tomada — responsável — prazo)', '(decisão tomada — responsável — prazo)'])}
<h2>Encerramento</h2>
<p>Nada mais havendo a tratar, a reunião foi encerrada e esta ata vai assinada pelos presentes.</p>
${assinatura('(nome e cargo)')}
`,
    },
    {
        id: 'pauta-reuniao',
        nome: 'Pauta de reunião',
        descricao: 'Só o roteiro da reunião, para enviar antes e conduzir na hora.',
        categoria: 'REUNIAO',
        campos: [
            { id: 'assunto', rotulo: 'Assunto', tipo: 'TEXTO' },
            { id: 'data', rotulo: 'Data', tipo: 'DATA' },
            { id: 'hora', rotulo: 'Hora', tipo: 'HORA' },
            { id: 'duracao', rotulo: 'Duração prevista', dica: 'Ex.: 1 hora.', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Pauta${v.assunto ? ` — ${v.assunto}` : ''}`,
        montar: (v) => `
<h1>Pauta da reunião</h1>
<p><strong>Assunto:</strong> ${ou(v.assunto, 'assunto')}</p>
<p><strong>Data e hora:</strong> ${ou(v.data, 'data')} às ${ou(v.hora, 'hora')} &nbsp;&nbsp; <strong>Duração:</strong> ${ou(v.duracao, 'duração')}</p>
<h2>Itens</h2>
${lista(['(item 1 — quem apresenta)', '(item 2 — quem apresenta)', '(item 3 — quem apresenta)'])}
<h2>O que decidir</h2>
${lista(['(decisão necessária)', '(decisão necessária)'])}
<h2>Preparar antes</h2>
${lista(['(documento ou dado que cada pessoa deve levar)'])}
`,
    },
    /* ---------------- Relatórios ---------------- */
    {
        id: 'relatorio-atividades',
        nome: 'Relatório de atividades',
        descricao: 'O que foi feito no período, resultados e o que vem depois.',
        categoria: 'RELATORIO',
        campos: [
            { id: 'responsavel', rotulo: 'Responsável', tipo: 'TEXTO' },
            { id: 'setor', rotulo: 'Setor ou projeto', tipo: 'TEXTO' },
            { id: 'periodo', rotulo: 'Período', dica: 'Ex.: outubro de 2026.', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Relatório de atividades${v.periodo ? ` — ${v.periodo}` : ''}`,
        montar: (v) => `
<h1>Relatório de atividades</h1>
<p><strong>Responsável:</strong> ${ou(v.responsavel, 'nome')}</p>
<p><strong>Setor ou projeto:</strong> ${ou(v.setor, 'setor')}</p>
<p><strong>Período:</strong> ${ou(v.periodo, 'período')}</p>
<h2>Atividades realizadas</h2>
${lista(['(atividade — data — resultado)', '(atividade — data — resultado)', '(atividade — data — resultado)'])}
<h2>Resultados</h2>
<p><em>O que mudou com o trabalho do período. Números ajudam.</em></p>
<h2>Dificuldades</h2>
<p><em>O que atrapalhou e do que dependia.</em></p>
<h2>Próximos passos</h2>
${lista(['(o que fazer — até quando)'])}
<p style="text-align: right; margin-top: 2em;">${ou(v.responsavel, 'cidade')}, ${hoje()}</p>
${assinatura(esc(v.responsavel) || '(assinatura)')}
`,
    },
    {
        id: 'relatorio-visita',
        nome: 'Relatório de visita',
        descricao: 'Registro de visita técnica, inspeção ou acompanhamento em campo.',
        categoria: 'RELATORIO',
        campos: [
            { id: 'local', rotulo: 'Local visitado', tipo: 'TEXTO' },
            { id: 'data', rotulo: 'Data', tipo: 'DATA' },
            { id: 'responsavel', rotulo: 'Responsável pela visita', tipo: 'TEXTO' },
            { id: 'objetivo', rotulo: 'Objetivo', tipo: 'AREA' },
        ],
        titulo: (v) => `Relatório de visita${v.local ? ` — ${v.local}` : ''}`,
        montar: (v) => `
<h1>Relatório de visita</h1>
<p><strong>Local:</strong> ${ou(v.local, 'local')}</p>
<p><strong>Data:</strong> ${ou(v.data, 'data')}</p>
<p><strong>Responsável:</strong> ${ou(v.responsavel, 'nome')}</p>
<h2>Objetivo</h2>
<p>${ou(v.objetivo, 'por que a visita foi feita')}</p>
<h2>O que foi observado</h2>
<p><em>Descreva o que encontrou, em ordem.</em></p>
<h2>Pendências e providências</h2>
${lista(['(pendência — providência — responsável — prazo)'])}
<h2>Conclusão</h2>
<p><em>O que fica encaminhado.</em></p>
${assinatura(esc(v.responsavel) || '(assinatura)')}
`,
    },
    /* ---------------- Documentos ---------------- */
    {
        id: 'carta',
        nome: 'Carta',
        descricao: 'Carta formal: remetente, destinatário, assunto, corpo e fecho.',
        categoria: 'DOCUMENTO',
        campos: [
            { id: 'remetente', rotulo: 'Remetente', tipo: 'TEXTO' },
            { id: 'destinatario', rotulo: 'Destinatário', tipo: 'TEXTO' },
            { id: 'assunto', rotulo: 'Assunto', tipo: 'TEXTO' },
            { id: 'cidade', rotulo: 'Cidade', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Carta${v.assunto ? ` — ${v.assunto}` : ''}`,
        montar: (v) => `
<p style="text-align: right;">${ou(v.cidade, 'cidade')}, ${hoje()}.</p>
<p><strong>De:</strong> ${ou(v.remetente, 'nome do remetente')}<br>
<strong>Para:</strong> ${ou(v.destinatario, 'nome do destinatário')}<br>
<strong>Assunto:</strong> ${ou(v.assunto, 'assunto')}</p>
<p>Prezado(a) senhor(a),</p>
<p><em>Escreva aqui o motivo da carta: o que precisa, por quê, e o que espera como resposta.</em></p>
<p>Coloco-me à disposição para qualquer esclarecimento e aguardo retorno.</p>
<p>Atenciosamente,</p>
${assinatura(esc(v.remetente) || '(assinatura)')}
`,
    },
    {
        id: 'oficio',
        nome: 'Ofício',
        descricao: 'Comunicação oficial numerada, com destinatário e assunto.',
        categoria: 'DOCUMENTO',
        campos: [
            { id: 'numero', rotulo: 'Número do ofício', dica: 'Ex.: 12/2026.', tipo: 'TEXTO' },
            { id: 'destinatario', rotulo: 'Destinatário', tipo: 'TEXTO' },
            { id: 'assunto', rotulo: 'Assunto', tipo: 'TEXTO' },
            { id: 'remetente', rotulo: 'Setor ou pessoa que assina', tipo: 'TEXTO' },
            { id: 'cidade', rotulo: 'Cidade', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Ofício${v.numero ? ` nº ${v.numero}` : ''}`,
        montar: (v) => `
<p style="text-align: right;">${ou(v.cidade, 'cidade')}, ${hoje()}.</p>
<p><strong>Ofício nº ${ou(v.numero, 'número/ano')}</strong></p>
<p><strong>Ao(À):</strong> ${ou(v.destinatario, 'destinatário e cargo')}</p>
<p><strong>Assunto:</strong> ${ou(v.assunto, 'assunto')}</p>
<p>Senhor(a),</p>
<p><em>Escreva aqui o corpo do ofício: o que se comunica, solicita ou encaminha, e o que se espera.</em></p>
<p>Respeitosamente,</p>
${assinatura(esc(v.remetente) || '(nome, cargo e assinatura)')}
`,
    },
    {
        id: 'declaracao',
        nome: 'Declaração',
        descricao: 'Declaro, para os devidos fins, que…',
        categoria: 'DOCUMENTO',
        campos: [
            { id: 'declarante', rotulo: 'Quem declara', tipo: 'TEXTO' },
            { id: 'declarado', rotulo: 'Sobre quem ou o que se declara', tipo: 'TEXTO' },
            { id: 'finalidade', rotulo: 'Para que serve a declaração', tipo: 'TEXTO' },
            { id: 'cidade', rotulo: 'Cidade', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Declaração${v.declarado ? ` — ${v.declarado}` : ''}`,
        montar: (v) => `
<h1 style="text-align: center;">DECLARAÇÃO</h1>
<p>Eu, <strong>${ou(v.declarante, 'nome de quem declara')}</strong>, declaro para os devidos fins que
<strong>${ou(v.declarado, 'fato ou pessoa a que se refere')}</strong>.</p>
<p>A presente declaração é feita para ${ou(v.finalidade, 'finalidade')}.</p>
<p>Por ser expressão da verdade, firmo a presente.</p>
<p style="text-align: right; margin-top: 2em;">${ou(v.cidade, 'cidade')}, ${hoje()}.</p>
${assinatura(esc(v.declarante) || '(nome e assinatura)')}
`,
    },
    {
        id: 'recibo',
        nome: 'Recibo',
        descricao: 'Valor recebido, de quem, por quê e em que data.',
        categoria: 'DOCUMENTO',
        campos: [
            { id: 'recebedor', rotulo: 'Quem recebeu', tipo: 'TEXTO' },
            {
                id: 'pessoaRecebedor',
                rotulo: 'O recebedor é',
                tipo: 'ESCOLHA',
                opcoes: OPCOES_DE_PESSOA,
                opcional: true,
                dica: 'Opcional. Emitente pessoa jurídica costuma pôr o CNPJ perto da assinatura.',
            },
            {
                id: 'documentoRecebedor',
                rotulo: 'CPF ou CNPJ do recebedor',
                tipo: 'DOCUMENTO',
                pessoaDe: 'pessoaRecebedor',
                opcional: true,
                dica: 'Opcional. O número é conferido pelos dígitos verificadores.',
            },
            { id: 'pagador', rotulo: 'Quem pagou', tipo: 'TEXTO' },
            {
                id: 'pessoaPagador',
                rotulo: 'O pagador é',
                tipo: 'ESCOLHA',
                opcoes: OPCOES_DE_PESSOA,
                padrao: 'FISICA',
            },
            {
                id: 'documentoPagador',
                rotulo: 'CPF ou CNPJ do pagador',
                tipo: 'DOCUMENTO',
                pessoaDe: 'pessoaPagador',
                dica: 'CPF tem 11 dígitos, CNPJ tem 14. Número inválido não entra no recibo.',
            },
            {
                id: 'valor',
                rotulo: 'Valor',
                dica: 'Ex.: R$ 1.500,00. O recibo escreve o valor também por extenso.',
                tipo: 'TEXTO',
            },
            { id: 'referente', rotulo: 'Referente a', tipo: 'AREA' },
            { id: 'cidade', rotulo: 'Cidade', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Recibo${v.valor ? ` — ${v.valor}` : ''}`,
        montar: (v) => {
            /*
             * O valor aparece duas vezes: em número e em letras. É a praxe do recibo
             * brasileiro — se a vírgula do número estiver fora do lugar, é o extenso que
             * diz o que foi pago de verdade. Quando o que foi digitado não dá para ler
             * como quantia (`abc`, ou já escrito em letras), sai só o que a pessoa
             * escreveu, sem inventar valor.
             */
            const numero = esc(v.valor);
            const extenso = valorPorExtenso(v.valor ?? '');
            const quantia = numero
                ? `${numero}${extenso ? ` (<em>${extenso}</em>)` : ''}`
                : `<span style="color: #b45309;">[valor]</span>`;
            /* ", pessoa física, CPF 529.982.247-25," — ou nada, quando não há documento. */
            const doPagador = qualificacaoDaPessoa(v.pessoaPagador, v.documentoPagador);
            const qualificado = doPagador ? `, ${doPagador},` : '';
            return `
<h1 style="text-align: center;">RECIBO</h1>
<p>Recebi de <strong>${ou(v.pagador, 'nome de quem pagou')}</strong>${qualificado} a importância de
<strong>${quantia}</strong>, referente a ${ou(v.referente, 'motivo do pagamento')}.</p>
<p>Para maior clareza, firmo o presente recibo.</p>
<p style="text-align: right; margin-top: 2em;">${ou(v.cidade, 'cidade')}, ${hoje()}.</p>
${assinaturaDoRecibo(v)}
`;
        },
    },
    {
        id: 'requerimento',
        nome: 'Requerimento',
        descricao: 'Pedido formal a uma instituição ou órgão.',
        categoria: 'DOCUMENTO',
        campos: [
            { id: 'requerente', rotulo: 'Requerente', tipo: 'TEXTO' },
            { id: 'destinatario', rotulo: 'A quem se destina', tipo: 'TEXTO' },
            { id: 'pedido', rotulo: 'O que se pede', tipo: 'AREA' },
            { id: 'cidade', rotulo: 'Cidade', tipo: 'TEXTO' },
        ],
        titulo: (v) => 'Requerimento',
        montar: (v) => `
<h1 style="text-align: center;">REQUERIMENTO</h1>
<p><strong>Ao(À):</strong> ${ou(v.destinatario, 'destinatário')}</p>
<p><strong>Requerente:</strong> ${ou(v.requerente, 'nome completo')}</p>
<p>${ou(v.pedido, 'descreva o pedido, com a justificativa')}</p>
<p>Nestes termos, pede deferimento.</p>
<p style="text-align: right; margin-top: 2em;">${ou(v.cidade, 'cidade')}, ${hoje()}.</p>
${assinatura(esc(v.requerente) || '(nome e assinatura)')}
`,
    },
    /* ---------------- Escola e aula ---------------- */
    {
        id: 'plano-de-aula',
        nome: 'Plano de aula',
        descricao: 'Objetivo, conteúdo, metodologia e avaliação da aula.',
        categoria: 'ESCOLA',
        campos: [
            { id: 'professor', rotulo: 'Professor(a)', tipo: 'TEXTO' },
            { id: 'disciplina', rotulo: 'Disciplina', tipo: 'TEXTO' },
            { id: 'turma', rotulo: 'Turma', tipo: 'TEXTO' },
            { id: 'data', rotulo: 'Data da aula', tipo: 'DATA' },
            { id: 'tema', rotulo: 'Tema', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Plano de aula${v.tema ? ` — ${v.tema}` : ''}`,
        montar: (v) => `
<h1>Plano de aula</h1>
<table style="border-collapse: collapse; width: 100%;">
<tbody>
<tr><td style="border: 1px solid #bbb; padding: 6px;"><strong>Professor(a)</strong></td><td style="border: 1px solid #bbb; padding: 6px;">${ou(v.professor, 'nome')}</td></tr>
<tr><td style="border: 1px solid #bbb; padding: 6px;"><strong>Disciplina</strong></td><td style="border: 1px solid #bbb; padding: 6px;">${ou(v.disciplina, 'disciplina')}</td></tr>
<tr><td style="border: 1px solid #bbb; padding: 6px;"><strong>Turma</strong></td><td style="border: 1px solid #bbb; padding: 6px;">${ou(v.turma, 'turma')}</td></tr>
<tr><td style="border: 1px solid #bbb; padding: 6px;"><strong>Data</strong></td><td style="border: 1px solid #bbb; padding: 6px;">${ou(v.data, 'data')}</td></tr>
</tbody>
</table>
<h2>Tema</h2>
<p>${ou(v.tema, 'tema da aula')}</p>
<h2>Objetivo</h2>
<p><em>O que o aluno deve saber fazer ao fim da aula.</em></p>
<h2>Conteúdo</h2>
${lista(['(tópico 1)', '(tópico 2)'])}
<h2>Metodologia</h2>
<p><em>Como a aula acontece: exposição, atividade em grupo, exercício, vídeo…</em></p>
<h2>Recursos</h2>
${lista(['(material necessário)'])}
<h2>Avaliação</h2>
<p><em>Como saber se o objetivo foi alcançado.</em></p>
`,
    },
    {
        id: 'lista-presenca',
        nome: 'Lista de presença',
        descricao: 'Tabela com nome, documento e assinatura, para evento, curso ou reunião.',
        categoria: 'ESCOLA',
        campos: [
            { id: 'evento', rotulo: 'Evento, curso ou reunião', tipo: 'TEXTO' },
            { id: 'data', rotulo: 'Data', tipo: 'DATA' },
            { id: 'local', rotulo: 'Local', tipo: 'TEXTO' },
            { id: 'linhas', rotulo: 'Quantas linhas', dica: 'Padrão: 15.', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Lista de presença${v.evento ? ` — ${v.evento}` : ''}`,
        montar: (v) => {
            const pedidas = Number.parseInt((v.linhas ?? '').trim(), 10);
            const quantidade = Number.isFinite(pedidas) ? Math.min(Math.max(pedidas, 1), 60) : 15;
            const celula = 'border: 1px solid #bbb; padding: 8px; height: 22px;';
            const linhas = Array.from({ length: quantidade }, (_, indice) => `<tr><td style="${celula}">${indice + 1}</td><td style="${celula}"></td><td style="${celula}"></td><td style="${celula}"></td></tr>`).join('');
            return `
<h1>Lista de presença</h1>
<p><strong>Evento:</strong> ${ou(v.evento, 'evento')}</p>
<p><strong>Data:</strong> ${ou(v.data, 'data')} &nbsp;&nbsp; <strong>Local:</strong> ${ou(v.local, 'local')}</p>
<table style="border-collapse: collapse; width: 100%;">
<thead>
<tr>
<th style="${celula} text-align: left;">#</th>
<th style="${celula} text-align: left;">Nome completo</th>
<th style="${celula} text-align: left;">Documento</th>
<th style="${celula} text-align: left;">Assinatura</th>
</tr>
</thead>
<tbody>${linhas}</tbody>
</table>
`;
        },
    },
    /* ---------------- Pessoal ---------------- */
    {
        id: 'curriculo',
        nome: 'Currículo',
        descricao: 'Dados, objetivo, formação, experiência e habilidades.',
        categoria: 'PESSOAL',
        campos: [
            { id: 'nome', rotulo: 'Nome', tipo: 'TEXTO' },
            { id: 'cargo', rotulo: 'Cargo pretendido', tipo: 'TEXTO' },
            { id: 'contato', rotulo: 'Contato', dica: 'Telefone e e-mail.', tipo: 'TEXTO' },
            { id: 'cidade', rotulo: 'Cidade', tipo: 'TEXTO' },
        ],
        titulo: (v) => `Currículo${v.nome ? ` — ${v.nome}` : ''}`,
        montar: (v) => `
<h1 style="text-align: center;">${ou(v.nome, 'seu nome')}</h1>
<p style="text-align: center;">${ou(v.cargo, 'cargo pretendido')}<br>
${ou(v.contato, 'telefone e e-mail')} · ${ou(v.cidade, 'cidade')}</p>
<h2>Objetivo</h2>
<p><em>Duas linhas sobre o que você busca e o que tem a oferecer.</em></p>
<h2>Formação</h2>
${lista(['(curso — instituição — ano de conclusão)', '(curso — instituição — ano de conclusão)'])}
<h2>Experiência</h2>
<p><strong>(empresa) — (cargo)</strong><br><em>(período)</em></p>
${lista(['(o que fazia e o que entregou)'])}
<h2>Habilidades</h2>
${lista(['(habilidade)', '(habilidade)', '(habilidade)'])}
<h2>Cursos e certificados</h2>
${lista(['(curso — instituição — ano)'])}
`,
    },
];
/* ------------------------------------------------------------------ */
/* Uso                                                                */
/* ------------------------------------------------------------------ */
export function modeloDeTextoPorId(id) {
    if (!id)
        return null;
    return MODELOS_DE_TEXTO.find((modelo) => modelo.id === id) ?? null;
}
export function modelosDeTextoPorCategoria(categoria) {
    return MODELOS_DE_TEXTO.filter((modelo) => modelo.categoria === categoria);
}
function pessoaDaEscolha(valor) {
    return valor === 'FISICA' || valor === 'JURIDICA' ? valor : undefined;
}
/**
 * O que está errado no preenchimento — lista vazia quando está tudo certo.
 *
 * A conferência é do **servidor** (a rota chama esta função antes de montar), e a
 * tela chama a mesma função para avisar enquanto a pessoa digita. Uma regra só,
 * dois lugares: não há como a tela aceitar o que o servidor recusa.
 *
 * O que se confere hoje: o **documento** (CPF e CNPJ pelos dígitos verificadores,
 * e se o tipo bate com a escolha "pessoa física/jurídica") e a **escolha** que não
 * pode ficar vazia. Campo de texto livre não é conferido — não há o que conferir.
 */
export function validarValoresDoModelo(id, valores = {}) {
    const modelo = modeloDeTextoPorId(id);
    if (!modelo)
        return [];
    const problemas = [];
    for (const campo of modelo.campos) {
        const valor = (valores[campo.id] ?? '').trim();
        if (campo.tipo === 'DOCUMENTO') {
            if (!valor) {
                if (!campo.opcional) {
                    problemas.push({ campo: campo.id, rotulo: campo.rotulo, mensagem: 'Informe o CPF ou o CNPJ.' });
                }
                continue;
            }
            const esperada = campo.pessoaDe ? pessoaDaEscolha(valores[campo.pessoaDe]) : undefined;
            const mensagem = problemaDoDocumento(valor, esperada);
            if (mensagem)
                problemas.push({ campo: campo.id, rotulo: campo.rotulo, mensagem });
            continue;
        }
        if (campo.tipo === 'ESCOLHA' && !campo.opcional) {
            const escolhida = valor || campo.padrao || '';
            if (!campo.opcoes?.some((opcao) => opcao.valor === escolhida)) {
                problemas.push({ campo: campo.id, rotulo: campo.rotulo, mensagem: 'Escolha uma opção.' });
            }
        }
    }
    return problemas;
}
/**
 * Monta o texto a partir do modelo e do que a pessoa preencheu.
 *
 * Devolve `null` quando o modelo não existe — quem chama responde 400, em vez de
 * gravar um texto vazio sem ninguém perceber.
 */
export function montarTextoDoModelo(id, valores = {}) {
    const modelo = modeloDeTextoPorId(id);
    if (!modelo)
        return null;
    /*
     * A data e a hora chegam do `<input type="date">` no formato do navegador
     * (`2026-10-30`, `14:00`) — e ninguém escreve ata com data em ISO. A conversão
     * é feita aqui, uma vez, para todos os modelos.
     */
    const normalizados = { ...valores };
    for (const campo of modelo.campos) {
        const bruto = normalizados[campo.id];
        if (!bruto)
            continue;
        if (campo.tipo === 'DATA')
            normalizados[campo.id] = dataPorExtenso(bruto);
        if (campo.tipo === 'HORA')
            normalizados[campo.id] = horaDoBrasil(bruto);
    }
    const titulo = modelo.titulo(normalizados).slice(0, 160);
    return { titulo: titulo || modelo.nome, conteudo: modelo.montar(normalizados) };
}
/** `2026-10-30` → `30/10/2026`. O que não for ISO volta como veio. */
export function dataPorExtenso(valor) {
    const partes = valor.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : valor.trim();
}
/** `14:00` → `14h`; `14:30` → `14h30`. O que não for hora volta como veio. */
export function horaDoBrasil(valor) {
    const partes = valor.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!partes)
        return valor.trim();
    const minutos = partes[2] === '00' ? '' : partes[2];
    return `${partes[1]}h${minutos}`;
}
//# sourceMappingURL=modelos-de-texto.js.map